import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, Chip, DateText, EmptyState, ErrorNotice, Input, PageHeader, Pagination, Select, Skeleton, Stat, Table, Toolbar } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, qs } from '@/lib/api';
import { money } from '@/lib/format';
import type { PaymentsPage } from '@/lib/types';

const LIMIT = 50;

export function AdminPayments() {
  const { t, locale } = useI18n();
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const page = useQuery({ queryKey: ['admin', 'payments', kind, status, q, offset], queryFn: () => api.get<PaymentsPage>(`/api/admin/payments${qs({ kind, status, q, limit: LIMIT, offset })}`) });
  const s = page.data?.stats;
  const chip = (st: string) => <Chip form={st === 'paid' ? 'filled' : st === 'pending' ? 'outlined' : st === 'failed' ? 'warning' : 'muted'}>{t(`status.${st}`)}</Chip>;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.payments') }]} title={t('nav.payments')} />
      {s ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Stat label={t('admin.payments.monthMemberships')} value={money(s.membershipsHalalas, locale)} hint={t('admin.payments.totalN', { n: s.membershipsN })} />
          <Stat label={t('admin.payments.monthFees')} value={money(s.evaluationFeesHalalas, locale)} hint={`${t('admin.payments.ofWhichService')} ${money(s.serviceFeeHalalas, locale)}`} />
          <Stat label={t('admin.payments.commissionsDue')} value={money(s.commissionsDueHalalas, locale)} tone={s.commissionsDueN ? 'warning' : undefined} hint={t('admin.payments.transfersAwaiting', { n: s.commissionsDueN })} />
        </div>
      ) : null}
      <Toolbar end={page.data ? t('admin.payments.totalN', { n: page.data.items.length }) : ''}>
        <Select className="w-[180px]" value={kind} onChange={(e) => { setKind(e.target.value); setOffset(0); }} aria-label={t('app.kind')}>
          <option value="">{t('admin.payments.allKinds')}</option>
          {(['membership', 'evaluation_fee', 'commission'] as const).map((k) => <option key={k} value={k}>{t(`admin.payments.kinds.${k}`)}</option>)}
        </Select>
        <Select className="w-[180px]" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} aria-label={t('app.status')}>
          <option value="">{t('admin.payments.allStatuses')}</option>
          {(['paid', 'pending', 'failed', 'refunded'] as const).map((k) => <option key={k} value={k}>{t(`status.${k}`)}</option>)}
        </Select>
        <Input className="w-[240px] max-w-full" placeholder={t('admin.payments.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} aria-label={t('app.search')} />
      </Toolbar>
      {page.isLoading ? <Skeleton lines={8} /> : page.error ? <ErrorNotice error={page.error} retry={() => void page.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : page.data?.items.length ? (
        <Card className="p-0 overflow-hidden">
          <Table className="border-0 rounded-none">
            <thead><tr><th>{t('app.number')}</th><th>{t('app.kind')}</th><th>{t('admin.payments.payer')}</th><th>{t('app.amount')}</th><th>{t('admin.payments.method')}</th><th>{t('admin.payments.related')}</th><th>{t('app.status')}</th><th>{t('app.date')}</th></tr></thead>
            <tbody>
              {page.data.items.map((p) => (
                <tr key={p.id}>
                  <td className="num">{p.number}</td>
                  <td><Chip form="mint">{t(`admin.payments.kinds.${p.kind}`)}</Chip></td>
                  <td>{p.payerName ?? '—'}</td>
                  <td className="num">{money(p.amountHalalas, locale)}</td>
                  <td>{p.provider === 'bank_transfer' ? t('admin.payments.bankTransfer') : p.provider === 'moyasar' ? 'Moyasar' : p.provider}</td>
                  <td className="num">{p.related.kind === 'BR' ? <Link to={`/admin/broker-requests/${p.related.id}`}>{p.related.number}</Link> : p.related.kind === 'EV' ? <Link to={`/admin/evaluations/${p.related.id}`}>{p.related.number}</Link> : p.related.tier ? `${locale === 'ar' ? p.related.tier.nameAr : p.related.tier.nameEn}` : '—'}</td>
                  <td>{p.kind === 'commission' && p.status === 'pending' ? <Chip form="warning">{t('admin.payments.awaitingConfirm')}</Chip> : chip(p.status)}</td>
                  {/* `wrap`, like the audit table's timestamp: the date is the widest nowrap cell in the row, and
                      leaving it unbreakable puts the English table 23 px from its card at 1280 — one longer
                      status chip and it scrolls again. It only breaks when the column is actually squeezed. */}
                  <td className="text-muted"><DateText at={p.paidAt ?? p.createdAt} kind="date" wrap /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : <EmptyState title={t('app.noResults')} />}
      <Pagination offset={offset} limit={LIMIT} count={page.data?.items.length ?? 0} onChange={setOffset} labels={{ prev: t('app.prev'), next: t('app.next'), page: t('app.page') }} />
    </div>
  );
}
